import { NODE_BY_ID } from '../../data/nodes';
import { dieSidesLabel } from '../format';
import { dieArt } from '../diceArt';
import { icon } from '../icons';
import { containerSummary, hasContainer, nodeLevel } from '../gameApi';
import type { ScreenCtx } from '../types';

/**
 * Panneau « Gobelet » — COMMENTS v0.4.1 §1.2.
 *
 * v0.5.0 : le prestige a disparu, le contenant est **entièrement dérivé de
 * l'arbre de talents** (nombre de dés par *Gobelet* `ars_goblet`, part de d6 par
 * *Contenant hexagonal* `ars_hex`, tous deux dans la branche Arsenal). Il
 * survit donc aux runs comme le reste de l'arbre. Le panneau n'a aucune action :
 * il affiche le nombre de dés, la répartition d4/d6 et le rappel des deux
 * talents qui le font grandir.
 *
 * v0.15.0 (refonte F1, lot 2) : même information, panneau facetté, icônes et
 * vignettes SVG au lieu du texte seul.
 *
 * Monté dans l'onglet Dés de l'Accalmie ; renvoie `null` tant qu'il n'y a pas
 * de contenant (`game.hasContainer()`).
 */

/**
 * Talents qui pilotent le contenant, dans l'ordre d'affichage du rappel.
 * `whenOwned` : la ligne n'apparaît qu'une fois le nœud parent possédé (les
 * talents de fin d'arbre ne doivent pas encombrer le panneau dès le départ).
 */
const GROWTH_NODES: { id: string; hint: string; whenOwned?: string }[] = [
  { id: 'ars_goblet', hint: '+2 dés Basiques par niveau' },
  { id: 'ars_hex', hint: '+20 % de d6 par niveau' },
  // v0.26.0 (COMMENTS v0.25.0 §1.2).
  { id: 'ars_barrow', hint: '+1 000 dés Basiques par niveau', whenOwned: 'ars_bucket' },
  { id: 'ars_icosa', hint: '+20 % de d20 par niveau', whenOwned: 'ars_barrow' },
];

/** Nom du contenant (*Brouette* dès le niveau 1, sinon Seau, Bol, Gobelet). */
function containerTitle(ctx: ScreenCtx): string {
  const fn = (ctx.game as unknown as { containerName?: () => string | null }).containerName;
  return (typeof fn === 'function' ? fn.call(ctx.game) : null) ?? 'Gobelet';
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function buildContainerPanel(ctx: ScreenCtx): HTMLElement | null {
  const { game } = ctx;
  if (!hasContainer(game)) return null;

  // Les dés Basiques n'ont plus de rareté : on ne regroupe plus que par faces.
  const bySides = new Map<number, number>();
  for (const entry of containerSummary(game)) {
    bySides.set(entry.sides, (bySides.get(entry.sides) ?? 0) + entry.count);
  }
  // Repli sur les stats dérivées quand le cœur ne publie pas la synthèse mais
  // décrit déjà le contenant (`containerDiceCount` / `containerD6Count`).
  if (bySides.size === 0) {
    const derived = game.getDerivedStats() as unknown as {
      containerDiceCount?: number;
      containerD6Count?: number;
    };
    const total = Number.isFinite(derived.containerDiceCount) ? Number(derived.containerDiceCount) : 0;
    const d6 = Number.isFinite(derived.containerD6Count) ? Number(derived.containerD6Count) : 0;
    if (total > 0) {
      if (d6 > 0) bySides.set(6, Math.min(d6, total));
      if (total - d6 > 0) bySides.set(4, total - d6);
    }
  }
  const size = [...bySides.values()].reduce((sum, n) => sum + n, 0);

  // Charte F1 (v0.15.0) : panneau facetté, titre à icône, une vignette de dé
  // Basique (os, sans rareté) par ligne de faces.
  const section = el('section', 'container-panel f-panel');
  section.setAttribute('data-role', 'container');

  const head = el('div', 'container-head');
  const title = el('h2', 'stats-title');
  title.innerHTML = `${icon('cup')}<span data-role="container-name">${containerTitle(ctx)}</span>`;
  head.append(title);
  const count = el('span', 'container-count', `${size} dé${size > 1 ? 's' : ''}`);
  count.setAttribute('data-role', 'container-count');
  head.appendChild(count);
  section.appendChild(head);

  if (size === 0) {
    section.appendChild(
      el('p', 'container-empty', 'Contenant vide — montez le talent Gobelet (Arsenal).'),
    );
  } else {
    const list = el('ul', 'container-list');
    for (const sides of [...bySides.keys()].sort((a, b) => b - a)) {
      const n = bySides.get(sides) ?? 0;
      const li = el('li', 'container-entry');
      li.setAttribute('data-entry', `d${sides}`);
      const name = el('span', 'container-die');
      name.innerHTML = `${dieArt(sides, null, { size: 22, stroke: 3, plain: true })}<span>${dieSidesLabel(sides)} Basique</span>`;
      li.append(name, el('span', 'container-qty', `×${n}`));
      list.appendChild(li);
    }
    section.appendChild(list);
  }

  // ── Rappel : comment faire grandir le contenant ─────────────────────
  const growth = el('ul', 'container-growth');
  growth.setAttribute('data-role', 'container-growth');
  for (const g of GROWTH_NODES) {
    const def = NODE_BY_ID[g.id] as { name?: string; maxLevel?: number } | undefined;
    if (!def) continue; // nœud retiré par le cœur : rien à promettre au joueur
    if (g.whenOwned && nodeLevel(game, g.whenOwned) < 1) continue;
    const level = nodeLevel(game, g.id);
    const li = el('li', 'container-growth-row');
    li.setAttribute('data-growth', g.id);
    const name = el('span', 'container-growth-name');
    name.innerHTML = `${icon('node')}<span>${def.name ?? g.id} <b>Nv ${level}/${def.maxLevel ?? 5}</b></span>`;
    li.append(name, el('span', 'container-growth-hint', g.hint));
    growth.appendChild(li);
  }
  if (growth.childElementCount > 0) section.appendChild(growth);

  return section;
}
